import { Permission } from "@edgetier/watchtower-types";
import type { IPermissionGroup } from "./permission-groups.types";

/**
 * The heading shown above each group on the Roles & Permissions screen. Customers see these strings, so changing one
 * is a copy change. `permission-groups.test.ts` checks each one exactly.
 */
export enum PermissionGroupName {
    General = "General",
    AgentsAndActivity = "Agents & Activity",
    UsersAndRoles = "Users & Roles",
    ReviewsAndScorecards = "Reviews & Scorecards",
    Tags = "Tags",
    Interactions = "Interactions",
    Sonar = "Sonar",
    AskSpotlight = "Ask Spotlight",
    IntegrationsAndApi = "Integrations & API",
}

/**
 * The permission groups, in display order. Every permission belongs to exactly one group. A new permission must be
 * added to a group here or it won't appear on screen; `permission-groups.test.ts` fails if one is missed.
 */
export const PERMISSION_GROUPS = [
    {
        name: PermissionGroupName.General,
        permissions: [
            Permission.ViewExplore,
            Permission.ViewCustomReports,
            Permission.ViewCustomerInsights,
            Permission.ViewLive,
        ],
    },
    {
        name: PermissionGroupName.UsersAndRoles,
        permissions: [Permission.ManageUsers, Permission.ManageRoles, Permission.ManageTeams],
    },
    {
        name: PermissionGroupName.AgentsAndActivity,
        permissions: [
            Permission.ViewAgent,
            Permission.ViewAgentDetails,
            Permission.ViewOwnAgentActivity,
            Permission.ManageAgentTags,
        ],
    },
    {
        name: PermissionGroupName.AskSpotlight,
        permissions: [
            Permission.UseAskSpotlight,
            Permission.ManageAiRules,
            Permission.ViewAskSpotlightUsage,
            Permission.ShareAskSpotlightTemplates,
        ],
    },
    {
        name: PermissionGroupName.IntegrationsAndApi,
        permissions: [Permission.UseMcp],
    },
    {
        name: PermissionGroupName.Interactions,
        permissions: [
            Permission.AddOrRemoveEmotions,
            Permission.ManageWritingIssues,
            Permission.PlayAllCallRecordings,
            Permission.PlayOwnCallRecordings,
            Permission.DownloadInteractions,
            Permission.ExportInteractionData,
        ],
    },
    {
        name: PermissionGroupName.ReviewsAndScorecards,
        permissions: [
            Permission.ManageAllReviews,
            Permission.ViewAllReviews,
            Permission.ManageOwnReviews,
            Permission.ViewReceivedReviews,
            Permission.ManageScorecards,
        ],
    },
    {
        name: PermissionGroupName.Sonar,
        permissions: [Permission.ViewAnomalies, Permission.ManageAnomalyModels],
    },
    {
        name: PermissionGroupName.Tags,
        permissions: [
            Permission.ManageInteractionTags,
            Permission.ManagePhraseTriggers,
            Permission.TagPastInteractions,
            Permission.ManageAiTags,
        ],
    },
] as const satisfies readonly IPermissionGroup[];
